import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Wordmark } from "@/components/ControleFinanceiro";

const CSS = `
.cfg{
  --ink:#0B1220; --sub:rgba(42,48,64,.74); --glass:rgba(255,255,255,.62); --glass2:rgba(255,255,255,.46);
  --stroke:rgba(255,255,255,.72); --sep:rgba(60,60,67,.17); --blue:#007AFF; --wm1:#16203A; --wm2:#41537A;
  --bgGrad:linear-gradient(180deg,#EAF1FB 0%,#F0EEF9 45%,#EBF3F1 100%);
  --cardShadow:0 12px 36px rgba(40,60,110,.13),inset 0 1px 0 rgba(255,255,255,.9);
  --b1c:#AFCBFF; --b2c:#B6F0DC;
}
.cfg[data-theme="dark"]{
  --ink:#F2F4F8; --sub:rgba(220,226,238,.66); --glass:rgba(38,44,58,.55); --glass2:rgba(46,53,70,.45);
  --stroke:rgba(255,255,255,.14); --sep:rgba(255,255,255,.12); --blue:#0A84FF; --wm1:#E8ECF6; --wm2:#9AA8C8;
  --bgGrad:linear-gradient(180deg,#0B0F16 0%,#0E1220 45%,#0B1412 100%);
  --cardShadow:0 14px 40px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.1);
  --b1c:#1B3A6B; --b2c:#134B3A;
}
.cfg{min-height:100vh;color:var(--ink);font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text",sans-serif;
  background:var(--bgGrad);
  padding:env(safe-area-inset-top) 0 calc(40px + env(safe-area-inset-bottom));font-size:17px;-webkit-font-smoothing:antialiased}
.cfg *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
.cfg .wrap{max-width:560px;margin:0 auto;padding:0 16px;position:relative;z-index:1}
.cfg .blobs{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
.cfg .blob{position:absolute;border-radius:50%;filter:blur(75px);opacity:.55}
.cfg .card{background:var(--glass);border:1px solid var(--stroke);border-radius:22px;overflow:hidden;
  backdrop-filter:blur(26px) saturate(180%);-webkit-backdrop-filter:blur(26px) saturate(180%);
  box-shadow:var(--cardShadow);margin-bottom:14px}
.cfg .sec{font-size:13px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.06em;margin:22px 6px 8px}
.cfg .row{display:flex;align-items:center;gap:12px;padding:15px 18px;min-height:56px;position:relative;font-size:16px}
.cfg .row+.row::before{content:"";position:absolute;top:0;left:18px;right:0;height:1px;background:var(--sep)}
.cfg .sub{font-size:13px;color:var(--sub);margin-top:2px}
.cfg .right{margin-left:auto;text-align:right}
.cfg .btn{display:flex;align-items:center;justify-content:center;width:100%;padding:15px;min-height:52px;border:none;border-radius:16px;
  font-size:16px;font-weight:700;cursor:pointer;font-family:inherit}
.cfg .btn-blue{color:#fff;background:linear-gradient(180deg,#2E96FF,#0071E8);box-shadow:0 10px 26px rgba(0,113,232,.35),inset 0 1.5px 0 rgba(255,255,255,.5)}
.cfg .btn-danger{color:#D70015;background:rgba(255,59,48,.1);border:1px solid rgba(255,59,48,.25)}
.cfg .link{color:var(--blue);background:none;border:none;font-size:16px;font-weight:650;cursor:pointer;font-family:inherit;padding:8px;margin:-8px}
.cfg .pill{font-size:12px;font-weight:700;border-radius:99px;padding:4px 11px}
.cfg .pill.ok{color:#248A3D;background:rgba(52,199,89,.16)}
.cfg .pill.warn{color:#8a5200;background:rgba(255,149,0,.16)}
.cfg .toggle{width:50px;height:30px;border-radius:99px;border:none;cursor:pointer;position:relative;transition:background .2s;flex-shrink:0}
.cfg .toggle::after{content:"";position:absolute;top:2px;left:2px;width:26px;height:26px;border-radius:99px;background:#fff;
  box-shadow:0 2px 6px rgba(0,0,0,.25);transition:transform .2s}
.cfg .toggle.on{background:#34C759}.cfg .toggle.on::after{transform:translateX(20px)}
.cfg .toggle.off{background:rgba(120,120,128,.28)}
.cfg .head{display:flex;align-items:center;gap:12px;padding:18px 0 6px}
.cfg .title{font-size:clamp(28px,7.5vw,34px);font-weight:800;padding:10px 0 14px}
`;

export default function ConfigPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [assinatura, setAssinatura] = useState(null);
  const [dados, setDados] = useState(null);
  const [confirma, setConfirma] = useState(false);
  const [verHist, setVerHist] = useState(false);
  const [buscaHist, setBuscaHist] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      setUser(session.user);
      const { data: sub } = await supabase.from("subscriptions")
        .select("status,trial_ends_at").eq("user_id", session.user.id).maybeSingle();
      setAssinatura(sub);
      const { data: row } = await supabase.from("controle_app")
        .select("data").eq("user_id", session.user.id).maybeSingle();
      setDados(row?.data || null);
    })();
  }, []);

  const salvarDados = async (novo) => {
    setDados(novo);
    await supabase.from("controle_app").upsert(
      { user_id: user.id, data: novo, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    );
  };

  const alternarFluxo = (id) => {
    const novo = JSON.parse(JSON.stringify(dados));
    const c = novo.caixinhas.find((x) => x.id === id);
    if (c) c.noFluxo = !c.noFluxo;
    salvarDados(novo);
  };

  const exportar = () => {
    const blob = new Blob([JSON.stringify(dados, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `conta-em-dia-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const apagarTudo = async () => {
    await supabase.from("controle_app").delete().eq("user_id", user.id);
    setDados(null); setConfirma(false);
    setMsg("Dados apagados. Ao voltar, você recomeça do zero.");
  };

  const sair = async () => { await supabase.auth.signOut(); navigate("/"); };

  const sysDark = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const temaEfetivo = dados?.tema === "claro" ? "light" : dados?.tema === "escuro" ? "dark" : sysDark ? "dark" : "light";

  const trialAtivo = assinatura?.status === "trial" && assinatura?.trial_ends_at && new Date() < new Date(assinatura.trial_ends_at);
  const diasTrial = trialAtivo ? Math.ceil((new Date(assinatura.trial_ends_at) - new Date()) / 86400000) : 0;

  return (
    <div className="cfg" data-theme={temaEfetivo}>
      <style>{CSS}</style>
      <div className="blobs" aria-hidden>
        <div className="blob" style={{ width: 420, height: 420, background: "var(--b1c)", top: -120, left: -90 }} />
        <div className="blob" style={{ width: 460, height: 460, background: "var(--b2c)", bottom: -140, right: -80 }} />
      </div>
      <div className="wrap">
        <div className="head">
          <button className="link" onClick={() => navigate("/dashboard")}>‹ Voltar</button>
          <span style={{ marginLeft: "auto" }}><Wordmark size={13} /></span>
        </div>
        <div className="title">Ajustes</div>

        <div className="sec">Conta</div>
        <div className="card">
          <div className="row">
            {user?.user_metadata?.avatar_url && <img src={user.user_metadata.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: 99, border: "1.5px solid rgba(255,255,255,.72)" }} />}
            <div>
              <div style={{ fontWeight: 700 }}>{user?.user_metadata?.full_name || "Sua conta"}</div>
              <div className="sub">{user?.email}</div>
            </div>
          </div>
          <div className="row">
            <span>Sessão</span>
            <button className="link" style={{ marginLeft: "auto", color: "#D70015" }} onClick={sair}>Sair da conta</button>
          </div>
        </div>

        <div className="sec">Assinatura</div>
        <div className="card">
          <div className="row">
            <div>
              <div style={{ fontWeight: 700 }}>Plano Conta em Dia</div>
              <div className="sub">{assinatura?.status === "active" ? "Renovação mensal" : trialAtivo ? `Período de teste: ${diasTrial} ${diasTrial === 1 ? "dia restante" : "dias restantes"}` : "Nenhum plano ativo"}</div>
            </div>
            <span className={"pill right " + (assinatura?.status === "active" || trialAtivo ? "ok" : "warn")}>
              {assinatura?.status === "active" ? "Ativa" : trialAtivo ? "Teste" : "Inativa"}
            </span>
          </div>
          <div className="row">
            <span>Alterar ou cancelar</span>
            <button className="link" style={{ marginLeft: "auto" }} onClick={() => navigate("/planos")}>Abrir planos ›</button>
          </div>
        </div>

        {dados && (
          <>
            <div className="sec">Caixinhas no fluxo de caixa</div>
            <div className="card">
              {dados.caixinhas.map((c) => (
                <div className="row" key={c.id}>
                  <div>
                    <div style={{ fontWeight: 650 }}>{c.nome}</div>
                    <div className="sub">{c.noFluxo ? "Soma no saldo disponível do dia" : "Fora do fluxo de caixa"}</div>
                  </div>
                  <button className={"toggle " + (c.noFluxo ? "on" : "off")} style={{ marginLeft: "auto" }} onClick={() => alternarFluxo(c.id)} aria-label={c.nome} />
                </div>
              ))}
              <div className="row"><div className="sub">O saldo do fluxo é a soma das caixinhas ligadas acima. No padrão, Dia a dia e Conta.</div></div>
            </div>
          </>
        )}

        <div className="sec">Histórico</div>
        <div className="card">
          <div className="row">
            <div>
              <div style={{ fontWeight: 650 }}>Atualizações das caixinhas</div>
              <div className="sub">{dados ? new Set((dados.historico || []).map((h) => h.data)).size + " dias registrados" : "Sem dados"}</div>
            </div>
            <button className="link" style={{ marginLeft: "auto" }} onClick={() => setVerHist(!verHist)}>{verHist ? "Fechar" : "Abrir"}</button>
          </div>
          {verHist && dados && (
            <div style={{ padding: "0 18px 16px" }}>
              <input className="inp" placeholder="Buscar caixinha" value={buscaHist} onChange={(e) => setBuscaHist(e.target.value)}
                style={{ width: "100%", background: "var(--glass2)", border: "1px solid var(--stroke)", color: "var(--ink)", borderRadius: 12, padding: "11px 14px", fontSize: 16, fontFamily: "inherit", marginBottom: 12 }} />
              {Object.entries((dados.historico || []).reduce((m, h) => { (m[h.data] = m[h.data] || []).push(h); return m; }, {}))
                .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                .map(([dia, itens]) => [dia, itens.filter((h) => !buscaHist || h.caixinha.toLowerCase().includes(buscaHist.toLowerCase()))])
                .filter(([, itens]) => itens.length)
                .slice(0, 40)
                .map(([dia, itens]) => {
                  const soma = itens.reduce((s, h) => s + (h.novo - h.anterior), 0);
                  const [y, m, dd] = dia.split("-");
                  return (
                    <div key={dia} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, color: "var(--sub)", marginBottom: 5 }}>
                        <span>{dd}/{m}/{y}</span>
                        <span style={{ color: soma >= 0 ? "#248A3D" : "#D70015" }}>
                          {soma >= 0 ? "+" : ""}{soma.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </span>
                      </div>
                      {itens.map((h, i) => {
                        const d = h.novo - h.anterior;
                        return (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "6px 0", borderBottom: "1px solid var(--sep)" }}>
                            <span>{h.caixinha}{h.transf ? " · transferência" : ""}</span>
                            <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 650, color: d > 0 ? "#248A3D" : d < 0 ? "#D70015" : "inherit" }}>
                              {d > 0 ? "+" : ""}{d.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        <div className="sec">Seus dados</div>
        <div className="card">
          <div className="row">
            <div>
              <div style={{ fontWeight: 650 }}>Exportar tudo</div>
              <div className="sub">Baixa um arquivo com caixinhas, fluxo, custos e histórico</div>
            </div>
            <button className="link" style={{ marginLeft: "auto" }} onClick={exportar} disabled={!dados}>Baixar</button>
          </div>
          <div className="row" style={{ display: "block" }}>
            {!confirma ? (
              <button className="btn btn-danger" onClick={() => setConfirma(true)}>Apagar todos os dados</button>
            ) : (
              <div>
                <div style={{ fontSize: 14, fontWeight: 650, marginBottom: 10 }}>Isso apaga tudo de forma permanente. Quer mesmo continuar?</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn" style={{ background: "rgba(120,120,128,.16)" }} onClick={() => setConfirma(false)}>Cancelar</button>
                  <button className="btn btn-danger" onClick={apagarTudo}>Apagar de vez</button>
                </div>
              </div>
            )}
            {msg && <div className="sub" style={{ marginTop: 10 }}>{msg}</div>}
          </div>
        </div>

        <div className="sec">Sobre</div>
        <div className="card">
          <div className="row"><span>Privacidade</span><button className="link" style={{ marginLeft: "auto" }} onClick={() => navigate("/privacidade")}>Ler ›</button></div>
          <div className="row"><span className="sub">Conta em Dia · feito para o seu dia a dia</span></div>
        </div>
      </div>
    </div>
  );
}
